import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Download, Eye, FileArchive, Send } from 'lucide-react';
import { Button, Modal, Toggle } from '@tes/ui';
import { api } from '../lib/api';
import { downloadWithAuth, slugify } from '../lib/download';
import { messageOf } from '../lib/errors';
import { useFormatDate } from '../lib/useSettings';
import { PdfPreviewModal } from '../components/PdfPreviewModal';
import { CopyLinkButton, SigningLinks } from '../components/SigningLinks';
import type { EnvelopeDetail as EnvelopeDetailData, EnvelopeStatus, SignerStatus } from '../lib/types';

const ENVELOPE_STYLE: Record<EnvelopeStatus, string> = {
  sent: 'bg-sky-100 text-sky-700',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-200 text-slate-600',
  expired: 'bg-slate-200 text-slate-600',
};

const SIGNER_STYLE: Record<SignerStatus, string> = {
  pending: 'bg-slate-100 text-slate-600',
  sent: 'bg-sky-100 text-sky-700',
  viewed: 'bg-amber-100 text-amber-700',
  completed: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700',
};

function StatusBadge({ status, style }: { status: string; style: string }) {
  return (
    <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${style}`}>{status}</span>
  );
}

/** Marks envelopes sent from the dashboard's Quick send rather than by n8n. */
function QuickSendBadge() {
  return (
    <span className="ml-2 rounded bg-brand-50 px-1.5 py-0.5 align-middle text-[10px] font-medium text-brand-700">
      Quick send
    </span>
  );
}

const ACTIVE: readonly EnvelopeStatus[] = ['sent', 'in_progress'];

type Doc = EnvelopeDetailData['documents'][number];

/** The best PDF a document has right now, or null once retention purged it. */
function bestFile(d: Doc): { type: 'signed' | 'generated'; label: string } | null {
  if (d.files.signed) return { type: 'signed', label: 'signed' };
  if (d.files.generated) return { type: 'generated', label: 'generated' };
  return null;
}

/** Shared "download the whole envelope as a ZIP" action with per-id busy state. */
function useZipDownload() {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const download = async (id: string, title: string) => {
    setBusyId(id);
    setError(null);
    try {
      await downloadWithAuth(api.envelopeZipPath(id), `${slugify(title)}.zip`);
    } catch (e) {
      setError(messageOf(e, 'Could not download the files'));
    } finally {
      setBusyId(null);
    }
  };
  return { download, busyId, error };
}

export default function EnvelopesPage() {
  const { data, isLoading, error } = useQuery({ queryKey: ['envelopes'], queryFn: api.listEnvelopes });
  const [selected, setSelected] = useState<string | null>(null);
  const fmt = useFormatDate();
  const zip = useZipDownload();

  return (
    <div>
      <h1 className="mb-5 text-lg font-semibold">Envelopes</h1>
      {error && <p className="mb-3 text-sm text-red-600">{messageOf(error, 'Could not load envelopes')}</p>}
      {zip.error && <p className="mb-3 text-sm text-red-600">{zip.error}</p>}
      {isLoading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : !data || data.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-400">
          No signing envelopes yet.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-4 py-2">Title</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Mode</th>
                <th className="px-4 py-2">Created</th>
                <th className="px-4 py-2 text-right">Files</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((e) => (
                <tr key={e.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelected(e.id)}>
                  <td className="px-4 py-2">
                    <div className="font-medium">
                      {e.title}
                      {e.source === 'dashboard' && <QuickSendBadge />}
                    </div>
                    <div className="font-mono text-[11px] text-slate-400">{e.id}</div>
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={e.status} style={ENVELOPE_STYLE[e.status]} />
                  </td>
                  <td className="px-4 py-2 text-slate-500">{e.orderMode}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-slate-500">{fmt.dateTime(e.createdAt)}</td>
                  <td className="px-4 py-2 text-right">
                    {(
                      <button
                        type="button"
                        className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40"
                        title="Download all files (ZIP)"
                        aria-label={`Download all files for ${e.title}`}
                        disabled={zip.busyId === e.id}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          void zip.download(e.id, e.title);
                        }}
                      >
                        <FileArchive size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selected && <EnvelopeDetail id={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function EnvelopeDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, error: loadError } = useQuery({ queryKey: ['envelope', id], queryFn: () => api.getEnvelope(id) });
  const { data: events } = useQuery({ queryKey: ['envelope-events', id], queryFn: () => api.envelopeEvents(id) });
  const [message, setMessage] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ doc: Doc; type: 'signed' | 'generated' } | null>(null);
  const [confirm, setConfirm] = useState<'resend' | 'cancel' | null>(null);
  const [rotate, setRotate] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);
  const [docBusy, setDocBusy] = useState<string | null>(null);
  const fmt = useFormatDate();
  const zip = useZipDownload();

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['envelope', id] });
    void qc.invalidateQueries({ queryKey: ['envelope-events', id] });
    void qc.invalidateQueries({ queryKey: ['envelopes'] });
  };

  const remind = useMutation({
    mutationFn: (newLinks: boolean) => api.remindEnvelope(id, newLinks),
    onSuccess: () => {
      setMessage(null);
      refresh();
    },
  });
  const cancel = useMutation({
    mutationFn: () => api.cancelEnvelope(id),
    onSuccess: () => {
      setConfirm(null);
      setMessage('Envelope cancelled — every signing link is now dead.');
      refresh();
    },
  });
  // A mutation rather than a bare async handler so a failed fetch of the
  // certificate URL is shown instead of vanishing as an unhandled rejection.
  const audit = useMutation({
    mutationFn: () => api.envelopeAudit(id),
    onSuccess: ({ auditUrl }) => window.open(auditUrl, '_blank', 'noopener'),
  });

  const downloadDoc = async (d: Doc) => {
    const file = bestFile(d);
    if (!file) return;
    setDocBusy(d.id);
    setDocError(null);
    try {
      await downloadWithAuth(
        api.documentDownloadPath(d.id, file.type),
        `${slugify(d.title)}${file.type === 'signed' ? '-signed' : ''}.pdf`,
      );
    } catch (e) {
      setDocError(messageOf(e, 'Could not download the document'));
    } finally {
      setDocBusy(null);
    }
  };

  const active = !!data && ACTIVE.includes(data.status);
  const anyFile = !!data?.documents.some((d) => bestFile(d));
  const actionError = cancel.error ?? audit.error ?? loadError;

  return (
    <Modal
      title={data?.title ?? 'Envelope'}
      headerExtra={
        data && (
          <>
            <StatusBadge status={data.status} style={ENVELOPE_STYLE[data.status]} />
            {data.source === 'dashboard' && <QuickSendBadge />}
          </>
        )
      }
      onClose={onClose}
      size="xl"
      footer={
        <>
          {anyFile && (
            <Button
              variant="secondary"
              onClick={() => data && void zip.download(data.id, data.title)}
              disabled={zip.busyId === id}
            >
              <FileArchive size={16} className="mr-1" />
              {zip.busyId === id ? 'Preparing…' : 'Download all (ZIP)'}
            </Button>
          )}
          {data?.status === 'completed' && (
            <Button variant="secondary" onClick={() => audit.mutate()} disabled={audit.isPending}>
              <Download size={16} className="mr-1" />
              {audit.isPending ? 'Fetching…' : 'Audit certificate'}
            </Button>
          )}
          {active && (
            <Button
              variant="outline"
              onClick={() => {
                setRotate(false);
                setConfirm('resend');
              }}
              disabled={remind.isPending}
            >
              <Send size={16} className="mr-1" /> Resend links
            </Button>
          )}
          {active && (
            <Button variant="danger" onClick={() => setConfirm('cancel')} disabled={cancel.isPending}>
              <Ban size={16} className="mr-1" /> Cancel
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-5 text-sm">
        {message && <div className="rounded-lg bg-emerald-50 px-3 py-2 text-emerald-700">{message}</div>}
        {(actionError || zip.error || docError) && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-red-700">
            {zip.error ?? docError ?? messageOf(actionError, 'Something went wrong')}
          </div>
        )}

        {remind.data && <ResentLinks result={remind.data} />}

        {(data?.subject || data?.message) && (
          <section>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
              Invite email
            </h3>
            <div className="rounded-lg border border-slate-100 px-3 py-2">
              {data.subject && <div className="font-medium">{data.subject}</div>}
              {data.message && <p className="mt-1 whitespace-pre-line text-slate-600">{data.message}</p>}
            </div>
          </section>
        )}

        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Signers</h3>
          <div className="space-y-1">
            {data?.signers.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
                <div>
                  <span className="font-medium">{s.name}</span>{' '}
                  <span className="text-slate-400">&lt;{s.email}&gt; · {s.roleKey}</span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {s.signingUrl && <CopyLinkButton url={s.signingUrl} />}
                  <StatusBadge status={s.status} style={SIGNER_STYLE[s.status]} />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Documents</h3>
          <div className="space-y-1">
            {data?.documents.map((d) => {
              const file = bestFile(d);
              return (
                <div
                  key={d.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">{d.title}</div>
                    <div className="truncate font-mono text-[11px] text-slate-400">
                      {d.documentHash ? d.documentHash : d.id}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {file ? (
                      <>
                        <span className="mr-1 text-[11px] text-slate-400">{file.label}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPreview({ doc: d, type: file.type })}
                          title="Preview"
                        >
                          <Eye size={16} className="mr-1" /> Preview
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => void downloadDoc(d)}
                          disabled={docBusy === d.id}
                          title="Download PDF"
                        >
                          <Download size={16} className="mr-1" /> {docBusy === d.id ? '…' : 'Download'}
                        </Button>
                      </>
                    ) : (
                      <span className="text-[11px] text-slate-400">files purged</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Audit trail
          </h3>
          <div className="space-y-1 rounded-lg bg-slate-900 p-3 font-mono text-[11px] text-slate-100">
            {events?.length ? (
              events.map((e) => (
                <div key={e.id} className="truncate" title={new Date(e.at).toISOString()}>
                  {fmt.timestamp(e.at)} · {e.type} {e.ip ? `(${e.ip})` : ''}
                </div>
              ))
            ) : (
              <div className="text-slate-500">No events.</div>
            )}
          </div>
        </section>
      </div>

      {preview && (
        <PdfPreviewModal
          title={`${preview.doc.title} (${preview.type})`}
          path={api.documentDownloadPath(preview.doc.id, preview.type)}
          downloadName={`${slugify(preview.doc.title)}${preview.type === 'signed' ? '-signed' : ''}.pdf`}
          onClose={() => setPreview(null)}
        />
      )}

      {confirm === 'resend' && (
        <ConfirmAction
          title="Resend signing links?"
          confirmLabel={rotate ? 'Issue new links' : 'Resend links'}
          danger={rotate}
          busy={remind.isPending}
          error={remind.error ? messageOf(remind.error, 'Could not resend links') : null}
          onClose={() => setConfirm(null)}
          onConfirm={() => remind.mutate(rotate, { onSuccess: () => setConfirm(null) })}
        >
          <p>
            Every outstanding signer is sent their link again — the <strong>same</strong> link they
            already have, so anything they were sent before keeps working.
          </p>
          <p className="mt-2">
            The links go to your n8n webhook endpoints subscribed to{' '}
            <code className="rounded bg-slate-100 px-1">envelope.reminder</code> to email, and are
            shown here to copy — this dashboard does not send email itself.
          </p>
          <Toggle
            className="mt-3"
            checked={rotate}
            onChange={setRotate}
            label="Issue new links instead"
            description="Every link sent before stops working. Use this if a link went to the wrong person."
          />
        </ConfirmAction>
      )}

      {confirm === 'cancel' && (
        <ConfirmAction
          title="Cancel this envelope?"
          confirmLabel="Cancel envelope"
          danger
          busy={cancel.isPending}
          error={cancel.error ? messageOf(cancel.error, 'Could not cancel') : null}
          onClose={() => setConfirm(null)}
          onConfirm={() => cancel.mutate()}
        >
          <p>
            The envelope is voided for everyone: all signing links die, nothing further can be
            signed, and n8n receives <code className="rounded bg-slate-100 px-1">envelope.cancelled</code>.
            This cannot be undone.
          </p>
        </ConfirmAction>
      )}
    </Modal>
  );
}

/** What a resend handed out — the same links, or new ones where they had to be replaced. */
function ResentLinks({ result }: { result: Awaited<ReturnType<typeof api.remindEnvelope>> }) {
  const notified = result.webhookEndpoints > 0;
  const total = result.signers.length;
  const fresh = result.signers.filter((s) => s.newLink).length;
  return (
    <SigningLinks signers={result.signers} tone={notified ? 'ok' : 'warn'}>
      {fresh === 0
        ? `${total} link${total === 1 ? '' : 's'} resent (unchanged).`
        : `${fresh} new link${fresh === 1 ? '' : 's'} issued — older links no longer work.`}{' '}
      {notified
        ? `Sent to ${result.webhookEndpoints} webhook endpoint${result.webhookEndpoints === 1 ? '' : 's'} for n8n to email.`
        : 'No webhook endpoint is subscribed to envelope.reminder — nobody will be emailed automatically. Copy the links and send them yourself.'}
    </SigningLinks>
  );
}

/** A yes/no confirmation for actions that are irreversible but don't warrant retyping a name. */
function ConfirmAction({
  title,
  children,
  confirmLabel,
  danger,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  title: string;
  children: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      title={title}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Keep as is
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} disabled={busy} data-autofocus>
            {busy ? 'Working…' : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-slate-700">{children}</div>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </Modal>
  );
}
